import React, { useEffect } from "react";
import { toast } from "sonner";
import { ChevronLeft, ShieldCheck } from "lucide-react";

const TOKEN_KEY = "planflow.auth_token";
const USER_KEY = "planflow.auth_user";
const RETURN_TOKEN_KEY = "pf_admin_return_token";
const RETURN_USER_KEY = "pf_admin_return_user";

// Height of the banner (h-8 + py-2 + border ≈ ~36px on desktop, 44px on
// mobile with wrapped text). Measured after render and published to a CSS
// variable so full-viewport pages (e.g. WorkspacePage) can subtract it
// from `100dvh` and keep floating footers (the page-nav pill) visible.
const BANNER_VAR = "--pf-imp-banner-h";

/**
 * Thin banner rendered above every page when the current session was
 * produced by a superadmin impersonation. Clicking "Return" restores
 * the original admin token from localStorage and reloads.
 */
export default function ImpersonationBanner() {
    const returnToken =
        typeof window !== "undefined"
            ? window.localStorage.getItem(RETURN_TOKEN_KEY)
            : null;

    // Publish the banner's actual rendered height to a CSS custom
    // property so pages that pin 100dvh can subtract it. Cleared on
    // unmount (returning to admin session).
    useEffect(() => {
        if (!returnToken) return undefined;
        const el = document.querySelector('[data-testid="impersonation-banner"]');
        const h = el ? el.getBoundingClientRect().height : 36;
        document.documentElement.style.setProperty(BANNER_VAR, `${Math.ceil(h)}px`);
        const onResize = () => {
            const cur = document.querySelector(
                '[data-testid="impersonation-banner"]',
            );
            const ch = cur ? cur.getBoundingClientRect().height : 36;
            document.documentElement.style.setProperty(
                BANNER_VAR,
                `${Math.ceil(ch)}px`,
            );
        };
        window.addEventListener("resize", onResize);
        return () => {
            window.removeEventListener("resize", onResize);
            document.documentElement.style.setProperty(BANNER_VAR, "0px");
        };
    }, [returnToken]);

    if (!returnToken) return null;

    const returnUserRaw =
        typeof window !== "undefined"
            ? window.localStorage.getItem(RETURN_USER_KEY)
            : null;
    let adminEmail = "superadmin";
    try {
        if (returnUserRaw) {
            adminEmail = JSON.parse(returnUserRaw).email || adminEmail;
        }
    } catch {
        /* ignore */
    }

    const endImpersonation = () => {
        try {
            localStorage.setItem(TOKEN_KEY, returnToken);
            if (returnUserRaw) localStorage.setItem(USER_KEY, returnUserRaw);
            localStorage.removeItem(RETURN_TOKEN_KEY);
            localStorage.removeItem(RETURN_USER_KEY);
            toast.success("Returned to superadmin session");
            window.location.href = "/admin";
        } catch (e) {
            toast.error("Couldn't restore admin session");
        }
    };

    return (
        <div
            data-testid="impersonation-banner"
            className="sticky top-0 z-50 bg-rose-500/15 border-b border-rose-500/40 backdrop-blur px-4 py-2 flex items-center gap-3 text-[12px]"
        >
            <ShieldCheck className="w-4 h-4 text-rose-300 shrink-0" />
            <span className="text-rose-100">
                You're impersonating a user. Admin session is safely stashed.
            </span>
            <button
                type="button"
                onClick={endImpersonation}
                data-testid="impersonation-banner-return"
                className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-rose-400/70 text-rose-100 hover:bg-rose-500/25 font-bold text-[11px] uppercase tracking-wider transition-colors"
            >
                <ChevronLeft className="w-3 h-3" />
                Return to {adminEmail}
            </button>
        </div>
    );
}
